import { Timer02Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { Tooltip } from '@pique/component';
import type React from 'react';

import * as styles from './PomodoroTimer.css';

export interface PomodoroTimerDisplayProps {
  isRunning: boolean;
  elapsedLabel: string;
  plannedLabel: string;
  menuOpen?: boolean;
  isOverPlan?: boolean;
  onToggle?: () => void;
  renderActual?: (node: React.ReactNode) => React.ReactNode;
  renderPlanned?: (node: React.ReactNode) => React.ReactNode;
  variant?: 'default' | 'popup';
  showTooltips?: boolean;
}

export const PomodoroTimerDisplay: React.FC<PomodoroTimerDisplayProps> = ({
  isRunning,
  elapsedLabel,
  plannedLabel,
  menuOpen,
  isOverPlan,
  onToggle,
  renderActual,
  renderPlanned,
  variant = 'default',
  showTooltips = false,
}) => {
  const actualNode = (
    <span className={styles.timerSpent}>{elapsedLabel}</span>
  );
  const plannedNode = (
    <span className={styles.timerPlanned}>{plannedLabel}</span>
  );
  const actualContent = renderActual ? renderActual(actualNode) : actualNode;
  const plannedContent = renderPlanned ? renderPlanned(plannedNode) : plannedNode;

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggle?.();
  };

  const controlButton = isRunning ? (
    <button
      type="button"
      className={styles.pauseButton}
      onClick={handleToggle}
      aria-label="Pause timer"
      data-tauri-drag-region="false"
    >
      <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
        <rect x="6" y="4" width="4" height="16" rx="1" />
        <rect x="14" y="4" width="4" height="16" rx="1" />
      </svg>
    </button>
  ) : (
    <button
      type="button"
      className={styles.playButton}
      onClick={handleToggle}
      aria-label="Start timer"
      data-testid="pomodoro-play-button"
      data-tauri-drag-region="false"
    >
      <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
        <path d="M8 5.14v14.72a1 1 0 001.5.86l11-7.36a1 1 0 000-1.72l-11-7.36a1 1 0 00-1.5.86z" />
      </svg>
    </button>
  );

  return (
    <div
      className={styles.container}
      data-testid="pomodoro-timer"
      data-timer-active={isRunning || undefined}
      data-menu-open={menuOpen || undefined}
      data-over-plan={isOverPlan ? true : undefined}
      data-variant={variant}
    >
      <div className={styles.timerDisplay}>
        {actualContent}
        <span className={styles.timerDivider}>/</span>
        {plannedContent}
      </div>
      {!isRunning && (
        <HugeiconsIcon
          icon={Timer02Icon}
          size={12}
          className={styles.clockIcon}
        />
      )}
      {isRunning ? (
        showTooltips ? (
          <Tooltip content="Pause focus" side="top">
            {controlButton}
          </Tooltip>
        ) : (
          controlButton
        )
      ) : (
        showTooltips ? (
          <Tooltip content="Start focus" side="top">
            {controlButton}
          </Tooltip>
        ) : (
          controlButton
        )
      )}
    </div>
  );
};
